import { defineStore } from 'pinia';
import type { User } from '~/types';
import { USER_ROLES } from '~/constants/auth';

// Mirrors the Pinia-persisted `auth.token` (localStorage) into a `token` cookie
// so server-side handlers and middleware (e.g. property-id-to-slug-redirect)
// can detect authenticated users on hard refresh / direct URL navigation.
// `httpOnly: false` matches the existing client-read architecture (the SPA
// reads the token to attach `Authorization` headers); revisit if we move to
// session cookies.
const TOKEN_COOKIE_NAME = 'token';
const TOKEN_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

export const useAuthStore = defineStore(
  'auth',
  () => {
    const token = ref<string | null>(null);
    const user = ref<User | null>(null);
    const agent = ref<User | null>(null);
    const isLoadingAgent = ref(false);
    const countryCode = ref<string | null>(null);
    const isAutoLoading = ref(false);

    // `useCookie()` must be called from a Nuxt context (setup or plugin);
    // capture the ref here once so login()/logout() can mutate it from
    // anywhere (mutation handlers, etc.) without re-invoking useCookie.
    // `secure` is HTTPS-only at the browser level — disable in dev (HTTP)
    // so the cookie is actually set on `localhost:3001`.
    const cookieToken = useCookie<string | null>(TOKEN_COOKIE_NAME, {
      maxAge: TOKEN_COOKIE_MAX_AGE,
      sameSite: 'lax',
      secure: !import.meta.dev,
      httpOnly: false,
      path: '/',
    });

    // SSR hydration + client init: read the cookie if Pinia persist hasn't
    // already populated `token` from localStorage. Backwards-compatible: if
    // localStorage already has a token, the persist plugin wins and we keep
    // the cookie in sync via `login()` calls.
    if (!token.value && cookieToken.value) {
      token.value = cookieToken.value;
    } else if (token.value && !cookieToken.value) {
      // Pre-existing localStorage session without cookie — mirror it now.
      cookieToken.value = token.value;
    }

    // Pinia's persist plugin hydrates `token` from localStorage AFTER this
    // setup function runs, so the init block above doesn't catch the
    // already-logged-in case on first page load. Watch for any subsequent
    // `token` change and keep the cookie in lock-step with it.
    watch(token, (next) => {
      if (cookieToken.value !== next) {
        cookieToken.value = next;
      }
    });

    // Impersonation ("view as"): stash the real admin session, swap to the agent.
    const impersonation = ref<{ agentUserId: string; agentName: string } | null>(null);
    const impersonatorToken = ref<string | null>(null);
    const impersonatorUser = ref<User | null>(null);
    const roleCookie = useCookie<string | null>('role', {
      maxAge: TOKEN_COOKIE_MAX_AGE,
      sameSite: 'lax',
      secure: !import.meta.dev,
      path: '/',
    });

    const startImpersonation = (newToken: string, agentUser: User) => {
      impersonatorToken.value = token.value;
      impersonatorUser.value = user.value;
      impersonation.value = {
        agentUserId: String(agentUser.id),
        agentName: `${agentUser.firstName ?? ''} ${agentUser.lastName ?? ''}`.trim() || (agentUser.email ?? 'agent'),
      };
      token.value = newToken;
      user.value = agentUser;
      cookieToken.value = newToken;
      roleCookie.value = 'AGENT';
    };

    const stopImpersonation = () => {
      // Both halves of the stash are required to restore the admin. If either is
      // missing (e.g. partially-wiped persisted state), hard-logout rather than
      // strand the session as the agent with no banner and no way back.
      if (impersonatorToken.value && impersonatorUser.value) {
        token.value = impersonatorToken.value;
        cookieToken.value = impersonatorToken.value;
        user.value = impersonatorUser.value;
        roleCookie.value = impersonatorUser.value.role ?? null;
        impersonation.value = null;
        impersonatorToken.value = null;
        impersonatorUser.value = null;
      } else {
        logout();
      }
    };

    const getIsImpersonating = computed(() => !!impersonation.value);

    const login = (newToken: string, newUser: User) => {
      token.value = newToken;
      user.value = newUser;
      cookieToken.value = newToken;
    };

    const logout = () => {
      token.value = null;
      user.value = null;
      agent.value = null;
      cookieToken.value = null;
      impersonation.value = null;
      impersonatorToken.value = null;
      impersonatorUser.value = null;
    };

    const updateUser = (newUser: User) => {
      user.value = { ...user.value, ...newUser };
    };

    const setCountryCode = (code: string) => {
      countryCode.value = code;
    };

    const updateAgent = (newAgent: User) => {
      agent.value = newAgent;
    };

    const updateAgentLoading = (loading: boolean) => {
      isLoadingAgent.value = loading;
    };

    const updateAutoLoading = (loading: boolean) => {
      isAutoLoading.value = loading;
    };

    const getIsLoadingAgent = computed(() => isLoadingAgent.value);
    const getIsAutoLoading = computed(() => isAutoLoading.value);
    const getIsAdmin = computed(() => user.value?.role === USER_ROLES.ADMIN);
    const getIsAccountManager = computed(() => user.value?.role === USER_ROLES.ACCOUNT_MANAGER);
    const getToken = computed(() => token.value);
    const getUser = computed(() => user.value);
    const getAgent = computed(() => agent.value);
    const getCountryCode = computed(() => countryCode.value);

    return {
      token,
      user,
      agent,
      isLoadingAgent,
      countryCode,
      isAutoLoading,
      login,
      logout,
      updateUser,
      updateAgent,
      updateAgentLoading,
      setCountryCode,
      updateAutoLoading,
      getIsLoadingAgent,
      getIsAdmin,
      getIsAccountManager,
      getToken,
      getUser,
      getAgent,
      getCountryCode,
      getIsAutoLoading,
      impersonation,
      impersonatorToken,
      impersonatorUser,
      startImpersonation,
      stopImpersonation,
      getIsImpersonating,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ['token', 'user', 'impersonation', 'impersonatorToken', 'impersonatorUser'],
    },
  },
);
